import React, { useEffect, useState, useMemo } from 'react';
import {
  User,
  Mail,
  Phone,
  NotebookText,
  ArrowLeft,
  PenSquare
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

// Intents:
// - public signup form to capture students, uses Form ID in the URL to determine which teacher the student is signing up for
// - form ID is set in teachers profile settings
// - This page is available without signing in and can be accessed by anyone with the link
// - the UI has to be simple enough to work on its own to be linked on instagram. Use new footer and header. lightweight and self sustaining
// - footer should say: powered by Kite (this application)
// - the request is sanitized and sent to the backend where the form ID will be used to determine the teacher
// - student has to enter info like: name lastname phone number, email, note (optional)
// - the backend url: crm.rivieraapps.com/api/public/signup-form

interface SignupFormData {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  note: string;
}

const FormSignupPublic: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<SignupFormData>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    note: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{success: boolean, message: string} | null>(null);

  // Validate form
  const isFormValid = useMemo(() => {
    return (
      formData.first_name.trim() &&
      formData.last_name.trim() &&
      formData.phone.trim() &&
      formData.email.trim() &&
      formData.email.includes('@')
    );
  }, [formData]);

  const domain = window.location.hostname;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    console.log("Form ID", formId);
    if (!isFormValid || !formId) return;
    console.log('Form is valid and form ID is present. Proceeding with submission...');

    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      console.log('Sending POST request to backend with form ID:', formId);
      const response = await fetch(`https://${domain}/api/signupform`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_id: formId,
          ...formData
        })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: 'Successfully signed up! Your teacher will contact you soon.'
        });
        setFormData({
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          note: ''
        });
      } else {
        setSubmitStatus({
          success: false,
          message: result.message || 'Failed to sign up. Please try again.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'Network error. Please check your connection.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof SignupFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="fade-in max-w-2xl mx-auto py-8 mx-3">

      {/* Header */}
      <section className="mb-10 text-center">
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl px-6 py-10 md:px-10 md:py-12">
          <div className="w-24 h-24 bg-primary/15 rounded-full mx-auto mb-6 flex items-center justify-center border-4 border-white/60 shadow-sm">
            <PenSquare className="w-12 h-12 text-primary" />
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-text mb-3">
            Welcome! Let's get you signed up!
          </h1>

          <p className="text-base md:text-lg text-text-light max-w-xl mx-auto mb-6">
            Fill in the form below to sign up with your teacher. Once you submit your
            details, your teacher will reach out to you to confirm the next steps and schedule.
          </p>

          <p className="text-sm text-text-light/80">
            This form is just the beginning — no payment is required at this stage.
          </p>
        </div>
      </section>

      {/* Signup Form */}
      <div className="card mb-8">
        <div className="flex items-center gap-3 p-6 pb-4 border-b border-border">
          <NotebookText className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-bold">Your Information</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-text-light mb-2">
                First Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800"
                  placeholder="John"
                  required
                />
                <User className="w-5 h-5 text-primary absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-text-light mb-2">
                Last Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800"
                  placeholder="Doe"
                  required
                />
                <User className="w-5 h-5 text-primary absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>

          {/* Contact Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-text-light mb-2">
                Phone Number
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800"
                  placeholder="+33 6 12 34 56 78"
                  required
                />
                <Phone className="w-5 h-5 text-primary absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-text-light mb-2">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 pl-10 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800"
                  placeholder="john.doe@example.com"
                  required
                />
                <Mail className="w-5 h-5 text-primary absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          </div>

          {/* Optional Note */}
          <div>
            <label className="block text-sm font-semibold text-text-light mb-2">
              Note (Optional)
            </label>
            <textarea
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 min-h-[100px]"
              placeholder="Any additional information you'd like to share..."
            />
          </div>

          {/* Submit Status */}
          {submitStatus && (
            <div
              className={`rounded-xl p-4 sm:p-5 success-pop ${
                submitStatus.success
                  ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-100'
                  : 'bg-red-50 text-red-900 dark:bg-red-900/20 dark:text-red-100'
              }`}
            >
              {submitStatus.success ? (
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 text-xl">🎉</div>
                  <div className="text-left">
                    <p className="text-sm font-semibold mb-1">
                      Thank you for signing up!
                    </p>
                    <p className="text-sm leading-relaxed">
                      {submitStatus.message || 'We received your details and will contact you soon as possible.'}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm font-semibold">{submitStatus.message}</p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className="btn btn-primary w-full py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin">⏳</span>
                Signing Up...
              </span>
            ) : (
              'Sign Up'
            )}
          </button>
        </form>
      </div>

      

      {/* Footer */}
      <div className="text-center text-text-light text-sm">
        <p>Powered by Kite</p>
      </div>
    </div>
  );
};

export default FormSignupPublic;